import React from 'react';
import moment from 'moment-timezone';
import { css, StyleSheet } from 'aphrodite';
import Accordion from '@material-ui/core/ExpansionPanel';
import AccordionSummary from '@material-ui/core/ExpansionPanelSummary';
import AccordionDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Helper } from '../../common/helpers/Helper';
import { AppConfig } from '../../AppConfig';
import { Stats } from '../../components/Stats';
import { AppUiHelper } from '../../helpers/AppUiHelper';
import { AppTheme } from '../../styles/AppTheme';
import { BaseEntityPage } from '../../common/components/BaseEntityPage';
import { TableComponent } from '../../common/components/TableComponent';
import { DateRangePicker } from '../../common/components/charts/DateRangePicker';
import { DP_DISPLAY_NAME_MAP } from '../../data/data';

const EXPAND_DURATION = 300;
const RENDER_RATE_ICON = require('../../assets/icons/partner-reports/served.svg');
const EXCHANGE_ORDER = ['PU', 'PO', 'IN'];
const DROP_REASON_ORDER = ['EXPLICITBLOCK', 'AUTOBLOCK', 'AI_ANALYSIS_PENDING', 'BITRATE_EXCEEDED', 'INVALID_SIZE'];
const DROP_REASON_LABELS = {
  EXPLICITBLOCK: 'BLANKET BLOCK',
  AUTOBLOCK: 'RESTRICTION TAG BLOCK',
  INVALID_SIZE: 'MEDIA ERROR',
};

export class ScreenOttRenderrate extends BaseEntityPage {
  pageKey = 'ScreenOttRenderrate';
  pageTitle = Helper.getString('screenOttRenderrate');
  orgId = this.props.match.params.orgId || 'PRODUCT';
  breadCrumbs = [
    { title: Helper.getString('home'), to: this.baseRoute() },
    { title: Helper.getString('screenOttRenderrate') },
  ];
  noAdd = true;

  constructor(props, options) {
    super(props, options);
    // Default to the last 7 days, ending on the previous CST day
    const lastDay = moment().tz(AppConfig.DEFAULT_ZONEINFO).subtract(1, 'day');
    this.state = {
      ...this.state,
      dateStart: lastDay.clone().subtract(6, 'day').format('YYYY-MM-DD'),
      dateEnd: lastDay.format('YYYY-MM-DD'),
      expandedDrops: {},
    };
    this.tableHeadersMap = this.makeTableHeaders();
    this.toFetch = this.makeFetches();
  }

  makeFetches = () => {
    const { dateStart, dateEnd } = this.state;
    return [{
      method: 'GET',
      paths: ['miscs', 'screen-ads-summary'],
      queryParams: {
        startPeriod: dateStart.replace(/-/g, ''),
        endPeriod: dateEnd.replace(/-/g, ''),
      },
    }];
  };

  makeTableHeaders = () => ({
    Date: (current) => moment(current.itemId, 'YYYYMMDD').format('YYYY-MM-DD'),
    Exchange: (current) => this.renderExchangeColumn(current, 'exchange'),
    Requests: (current) => this.renderExchangeColumn(current, 'requests'),
    Leased: (current) => this.renderExchangeColumn(current, 'leased'),
    Served: (current) => this.renderExchangeColumn(current, 'served'),
    'Popped (Render Rate)': (current) => this.renderExchangeColumn(current, 'popped'),
    Dropped: (current) => this.renderExchangeColumn(current, 'dropped'),
  });

  getDropReasons = (item) => ({
    AI_ANALYSIS_PENDING: 0,
    AUTOBLOCK: 0,
    BITRATE_EXCEEDED: 0,
    EXPLICITBLOCK: 0,
    INVALID_SIZE: 0,
    ...item.droppedReason,
  });

  formatRate = (value, base) => (base ? `${((value || 0) * 100 / base).toFixed(1)}%` : '-');

  formatPercent = (value, base) => `(${this.formatRate(value, base)})`;

  getExchangeOrder = (exchanges) => Object.keys(exchanges).sort((a, b) => {
    const aIndex = EXCHANGE_ORDER.indexOf(a.replace(/^adstatdaysummary/, ''));
    const bIndex = EXCHANGE_ORDER.indexOf(b.replace(/^adstatdaysummary/, ''));
    return (aIndex < 0 ? EXCHANGE_ORDER.length : aIndex) -
      (bIndex < 0 ? EXCHANGE_ORDER.length : bIndex) || a.localeCompare(b);
  });

  toggleDrops = (key) => {
    this.setState(prevState => ({
      expandedDrops: { ...prevState.expandedDrops, [key]: !prevState.expandedDrops[key] },
    }));
  };

  renderDropped = (item, reasons, key) => (
    <Accordion
      square
      elevation={0}
      expanded={!!this.state.expandedDrops[key]}
      onChange={() => this.toggleDrops(key)}
      TransitionProps={{ timeout: EXPAND_DURATION }}
      classes={{ root: css(Styles.accordion), expanded: css(Styles.accordionExpanded) }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize='small' />}
        classes={{
          root: css(Styles.accordionSummary),
          expanded: css(Styles.accordionSummary),
          content: css(Styles.accordionContent),
          expandIcon: css(Styles.accordionIcon),
        }}
      >
        <strong className={css(Styles.counter)}>
          {(item.dropped || 0).toLocaleString()}
          <span className={css(Styles.percent)}>{this.formatPercent(item.dropped, item.leased)}</span>
        </strong>
      </AccordionSummary>
      <AccordionDetails classes={{ root: css(Styles.accordionDetails) }}>
        <div className={css(Styles.reasons)}>
          {[...DROP_REASON_ORDER, ...Object.keys(reasons).filter(reason => !DROP_REASON_ORDER.includes(reason)).sort()].map(reason => (
            <div key={reason} className={css(Styles.reason)}>
              <span>{DROP_REASON_LABELS[reason] || reason.replace(/_/g, ' ')}</span>
              <span>
                {(reasons[reason] || 0).toLocaleString()}
                <span className={css(Styles.percent)}>{this.formatPercent(reasons[reason], item.dropped)}</span>
              </span>
            </div>
          ))}
        </div>
      </AccordionDetails>
    </Accordion>
  );

  renderExchangeColumn = (current, metric) => (
    <div className={css(Styles.exchanges)}>
      {[...this.getExchangeOrder(current.exchanges), 'total'].map(groupId => {
        const item = groupId === 'total' ? current.total : current.exchanges[groupId];
        const reasons = this.getDropReasons(item);
        const exchange = groupId.replace(/^adstatdaysummary/, '');
        const key = `${current.itemId}-${groupId}`;
        // Every column shares the same row height so exchanges line up across the table
        const height = this.state.expandedDrops[key] ? 68 + Object.keys(reasons).length * 20 : 48;
        return (
          <div
            key={groupId}
            className={css(Styles.exchange, groupId === 'total' && Styles.total)}
            style={{ height, transition: `height ${EXPAND_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)` }}
          >
            {metric === 'dropped'
              ? this.renderDropped(item, reasons, key)
              : <strong className={css(Styles.counter)}>
                {metric === 'exchange'
                  ? (groupId === 'total' ? 'Total' : DP_DISPLAY_NAME_MAP[exchange] || exchange)
                  : (item[metric] || 0).toLocaleString()}
                {metric === 'popped' &&
                  <span className={css(Styles.percent)}>{this.formatPercent(item.popped, item.leased)}</span>
                }
              </strong>
            }
          </div>
        );
      })}
    </div>
  );

  onFetchSuccess = (results) => {
    const items = (results[0] && results[0].items) || [];
    const dates = {};
    items.forEach(item => {
      if (!dates[item.itemId]) {
        dates[item.itemId] = { id: item.itemId, itemId: item.itemId, exchanges: {} };
      }
      dates[item.itemId].exchanges[item.groupId] = item;
    });
    Object.keys(dates).forEach(date => {
      const row = dates[date];
      row.total = { requests: 0, leased: 0, served: 0, popped: 0, dropped: 0, droppedReason: {} };
      Object.keys(row.exchanges).forEach(groupId => {
        const item = row.exchanges[groupId];
        ['requests', 'leased', 'served', 'popped', 'dropped'].forEach(metric => {
          row.total[metric] += item[metric] || 0;
        });
        Object.keys(item.droppedReason || {}).forEach(reason => {
          row.total.droppedReason[reason] = (row.total.droppedReason[reason] || 0) +
            (item.droppedReason[reason] || 0);
        });
      });
    });
    this.setState({
      items: Object.keys(dates).sort().reverse().map(date => dates[date]),
      stats: this.makeStats(items),
    });
  };

  // Rates for the whole period are summed counters, so busier days weigh more
  makeStats = (items) => {
    if (!items.length) {
      return null;
    }
    const groups = {};
    const total = { leased: 0, popped: 0, dropped: 0 };
    items.forEach(item => {
      groups[item.groupId] = groups[item.groupId] || { leased: 0, popped: 0, dropped: 0 };
      ['leased', 'popped', 'dropped'].forEach(metric => {
        groups[item.groupId][metric] += item[metric] || 0;
        total[metric] += item[metric] || 0;
      });
    });
    const makeTile = (title, group) => ({
      title,
      image: RENDER_RATE_ICON,
      value: (
        <div>
          <div>{this.formatRate(group.popped, group.leased)}</div>
          <div className={css(Styles.statsSubValue)}>
            {AppUiHelper.chipOrange(`Drop Rate: ${this.formatRate(group.dropped, group.leased)}`, false)}
          </div>
        </div>
      ),
    });
    return [
      ...this.getExchangeOrder(groups).map(groupId => {
        const exchange = groupId.replace(/^adstatdaysummary/, '');
        return makeTile(`${DP_DISPLAY_NAME_MAP[exchange] || exchange} Render Rate`, groups[groupId]);
      }),
      makeTile('Overall OTT Render Rate', total),
    ];
  };

  onFilter = ({ dateStart, dateEnd }) => {
    this.setState({ dateStart, dateEnd }, () => {
      this.toFetch = this.makeFetches();
      this.fetchItems();
    });
  };

  renderAboveTable = () => (
    <div className={css(Styles.aboveTable)}>
      <DateRangePicker
        buttonLabel={Helper.getString('filter')}
        onFilter={this.onFilter}
        dateStart={this.state.dateStart}
        dateEnd={this.state.dateEnd}
      />
      {this.state.stats &&
        <div>
          <p className={css(Styles.statsNote)}>The statistics shown are approximate and may vary by ±5%.</p>
          <Stats items={this.state.stats} />
        </div>
      }
    </div>
  );

  renderTable = () => (
    <TableComponent
      tableHeadersMap={this.tableHeadersMap}
      items={this.state.items}
    />
  );
}

const Styles = StyleSheet.create({
  aboveTable: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  statsNote: {
    margin: '0 0 10px',
    fontSize: 14,
    color: AppTheme.primaryColor,
  },
  statsSubValue: {
    marginTop: 8,
  },
  exchanges: {
    display: 'flex',
    flexDirection: 'column',
  },
  exchange: {
    boxSizing: 'border-box',
    overflow: 'hidden',
    padding: '12px 0',
    ':not(:last-child)': {
      borderBottom: '1px solid #eee',
    },
  },
  total: {
    borderTop: '2px solid #ddd',
  },
  counter: {
    display: 'block',
    lineHeight: '24px',
    whiteSpace: 'nowrap',
  },
  accordion: {
    background: 'transparent',
    margin: 0,
    ':before': {
      display: 'none',
    },
  },
  accordionExpanded: {
    margin: 0,
  },
  accordionSummary: {
    minHeight: 24,
    padding: 0,
  },
  accordionContent: {
    margin: 0,
  },
  accordionIcon: {
    padding: 0,
    marginRight: 0,
  },
  accordionDetails: {
    display: 'block',
    padding: 0,
  },
  reasons: {
    marginTop: 8,
    paddingTop: 8,
    borderTop: '1px solid #eee',
    fontSize: 11,
    minWidth: 200,
  },
  reason: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
  },
  percent: {
    display: 'inline-block',
    minWidth: 48,
    marginLeft: 8,
    textAlign: 'right',
    fontWeight: 'normal',
    color: '#888',
  },
});



// WEBPACK FOOTER //
// ./src/screens/admin/ScreenOttRenderrate.js